import { modelTitleState } from '@/components/canvas/utils/modelTitle'

import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import { type NodeProps, useReactFlow, useNodeConnections, useNodesData } from '@xyflow/react'
import { useParams } from '@tanstack/react-router'
import { ImageNode } from '@/components/canvas/nodes'
import type { GenieNodeState, AiModelPurpose } from '@genieartinc/schemas'
import type { NodeOrchestratorState } from '@genieartinc/schemas'
import { useJobGeneration } from '@/hooks/useJobGeneration'
import { useJobStore } from '@/stores/jobStore'
import { getInputNode } from '@/lib/canvas/utils/getInputNodeData'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import type { HandleMeta } from '@/lib/canvas/types'
import { PURPOSE_HANDLES } from '@/lib/canvas/configs/handles'
import { toast } from '@/components/ui/toast'
import { useNodeModels, getDefaultModel, findModelById } from '@/hooks/useNodeModels'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { usePinnedParameters } from '@/components/canvas/nodes/pinned-parameters/usePinnedParameters'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { useJobEstimate } from '@/hooks/useJobEstimate'
import { isApiError } from '@/lib/api/api-helpers'
import { useInsufficientBalanceStore } from '@/components/canvas/nodes/base/insufficient-balance-store'
import {
  executionForProvider,
  hydrateAttachments,
  planImageInputs,
  resolveImageInputCapability,
} from '@/lib/canvas/image-input-capability'
import { imageUrlFromFlowNode } from '@/lib/canvas/style-transfer-roles'
import { presentStyleTransfer, presentInpaint, ensureInpaintHandles, inpaintAlignmentBlock, isInpaintMaskParam, type StyleTransferPresentation, type InpaintPresentation } from '@genieartinc/schemas/pools/ai'
import { inpaintAssetFromNode } from '@/lib/canvas/inpaint-roles'

function loadImageSize(url: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img.naturalWidth > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : null)
    img.onerror = () => resolve(null)
    img.src = url
  })
}

/** Mask-editor paint is white on transparent. Put it on black so white is the region to change, not an alpha the model may ignore. */
async function flattenPaintedMask(url: string): Promise<string> {
  if (!url.startsWith('data:image/') || typeof document === 'undefined') return url
  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const el = new Image()
    el.onload = () => resolve(el.naturalWidth > 0 ? el : null)
    el.onerror = () => resolve(null)
    el.src = url
  })
  if (!img) return url
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return url
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0)
  return canvas.toDataURL('image/png')
}

/**
 * ImageNodeNode - React Flow custom node
 *
 * Replaces ImageNodeAdapter for the React Flow migration.
 * Wraps the existing ImageNode variant component with React Flow handles.
 */

export interface ImageNodeNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

/** Purpose-specific handle configurations - defined at module scope to avoid re-creation on every render */
/** Purpose-based handles from centralized config */
const HANDLES_BY_PURPOSE = PURPOSE_HANDLES.ImageNode

/** Fallback handles (same as create) */
const DEFAULT_IMAGE_HANDLES = HANDLES_BY_PURPOSE.create

function ImageNodeNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as ImageNodeNodeData
  const { state: genieState, onHoverChange } = nodeData
  const purpose = (genieState.purpose as string) || undefined
  const onStateChange = useNodeStateChange(id)
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = canvasIdParam as string | undefined

  const reactFlowInstance = useReactFlow()
  const { submitJob } = useJobGeneration()
  const loading = false // Generation is now async via worker
  const error = null
  const subPurpose = (genieState.subPurpose as string) || undefined
  const { data: rawImageModels } = useNodeModels('image', undefined, (purpose as AiModelPurpose) || 'create', subPurpose)
  // For edit purpose, only show general-purpose editors (no task-specific subPurpose like background-removal, try-on, etc.)
  const imageModels = useMemo(
    () => purpose === 'edit' ? rawImageModels.filter(m => !m.subPurpose) : rawImageModels,
    [rawImageModels, purpose]
  )
  const { balance: walletBalance } = useWalletBalance()

  const currentPromptRef = useRef(genieState.prompt || '')

  // Concurrent generation guard
  const isGeneratingRef = useRef(false)
  // genieState ref to prevent handleGenerate recreation
  const genieStateRef = useRef(genieState)
  genieStateRef.current = genieState
  // Ref for error-path-only value — avoids handleGenerate recreation on wallet polling
  const walletBalanceRef = useRef(walletBalance)
  walletBalanceRef.current = walletBalance

  const [orchestratorState, setOrchestratorState] = useState<NodeOrchestratorState>({
    status: 'idle',
  })

  // On mount: restore state from active jobs (handles navigate-away-and-back + stale locks)
  useEffect(() => {
    // Reset guard on mount to prevent permanently stuck state from previous lifecycle
    isGeneratingRef.current = false

    const store = useJobStore.getState()
    const jobs = Array.from(store.jobs.values())
    const activeJob = jobs.find(j => j.nodeId === id && (j.status === 'PENDING' || j.status === 'RUNNING'))
    const completedJob = jobs.find(j => j.nodeId === id && (j.status === 'COMPLETED' || j.status === 'FAILED'))

    if (activeJob) {
      // Job still running — restore working state and lock
      isGeneratingRef.current = true
      setOrchestratorState({ status: 'working', message: activeJob.message || 'Generating...', progress: activeJob.progress || 0 })
    } else if (completedJob) {
      // Job finished while away — clear any stale lock via bridge
      const bridge = useCanvasBridgeStore.getState()
      bridge.updateNodeStateCallback?.(id, { locked: false })
    }

    return () => { isGeneratingRef.current = false }
  }, [id])

  useEffect(() => {
    if (orchestratorState.status === 'success') {
      const timer = setTimeout(() => setOrchestratorState({ status: 'idle' }), 3000)
      return () => clearTimeout(timer)
    }
  }, [orchestratorState.status])

  // Safety timeout: auto-reset if stuck in 'working' state for 10 minutes
  useEffect(() => {
    if (orchestratorState.status !== 'working') return
    const timer = setTimeout(() => {
      isGeneratingRef.current = false
      setOrchestratorState({ status: 'error', message: 'Generation timed out — please try again' })
      const bridge = useCanvasBridgeStore.getState()
      bridge.updateNodeStateCallback?.(id, { locked: false })
    }, 10 * 60 * 1000)
    return () => clearTimeout(timer)
  }, [orchestratorState.status, id])

  // Subscribe to job store for async generation progress/completion
  useEffect(() => {
    const unsubscribe = useJobStore.subscribe(
      (state) => {
        const jobs = Array.from(state.jobs.values())
        const nodeJobs = jobs.filter(j => j.nodeId === id)
        return nodeJobs[nodeJobs.length - 1]
      },
      (latestJob) => {
        if (!latestJob) return
        if (latestJob.status === 'RUNNING' && latestJob.progress !== undefined) {
          setOrchestratorState({ status: 'working', message: latestJob.message || 'Generating...', progress: latestJob.progress })
        } else if (latestJob.status === 'COMPLETED') {
          isGeneratingRef.current = false
          setOrchestratorState({ status: 'success', message: 'Generated successfully', progress: 100 })
        } else if (latestJob.status === 'FAILED') {
          isGeneratingRef.current = false
          setOrchestratorState({ status: 'error', message: latestJob.error || 'Generation failed' })
        }
      },
    )
    return unsubscribe
  }, [id])

  // Reactively resolve connected data from upstream nodes
  // Uses useNodeConnections + useNodesData for proper reactivity when upstream state changes
  const incomingConnections = useNodeConnections({ handleType: 'target' })

  const sourceNodeIds = useMemo(
    () => incomingConnections.map(c => c.source),
    [incomingConnections],
  )

  const sourceNodesData = useNodesData(sourceNodeIds)

  // Derive connected image URLs from upstream image/file nodes. Every image counts;
  // taking only the last one used to drop the rest with no explanation.
  const connectedImageUrls = useMemo(() => {
    const urls: string[] = []
    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      const sourceState = (nodeEntry.data as Record<string, unknown>).state as Record<string, unknown> | undefined
      if (!sourceState) continue
      const sourceType = nodeEntry.type || ''
      if (sourceType === 'ImageFile') {
        const fileMeta = sourceState.fileMetadata as Record<string, unknown> | undefined
        if (typeof fileMeta?.publicUrl === 'string') urls.push(fileMeta.publicUrl)
      }
      if (sourceType === 'ImageNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const outputs = (createdItems ?? []).filter(item => item.type === 'image' && item.content).map(item => item.content)
        if (outputs.length > 0) urls.push(...outputs)
        else if (Array.isArray(sourceState.images)) {
          for (const image of sourceState.images) if (typeof image === 'string') urls.push(image)
        }
      } else if (Array.isArray(sourceState.images)) {
        for (const image of sourceState.images) if (typeof image === 'string') urls.push(image)
      }
    }
    return [...new Set(urls)]
  }, [sourceNodesData])
  const connectedImage = connectedImageUrls[0] ?? null

  // Derive connected prompt from upstream text nodes
  const connectedPrompt = useMemo(() => {
    if (sourceNodesData.length === 0) return null

    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      const sourceData = nodeEntry.data as Record<string, unknown>
      const sourceState = sourceData?.state as Record<string, unknown> | undefined
      if (!sourceState) continue

      const sourceType = nodeEntry.type || ''
      if (sourceType === 'TextFile' || sourceType === 'TextNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const latestText = createdItems?.[createdItems.length - 1]
        if (latestText?.type === 'text' && typeof latestText.content === 'string' && latestText.content.trim()) {
          return latestText.content
        }
        const prompt = sourceState.prompt
        if (typeof prompt === 'string' && prompt.trim()) return prompt
      }
    }
    return null
  }, [sourceNodesData])

  // Derive connected LoRAs from upstream LoraModel nodes. Each entry is `{id, scale, triggerWord}`.
  // Later merged into `settings.loras` on generate.
  const connectedLoras = useMemo(() => {
    if (sourceNodesData.length === 0) return [] as Array<{ id: string; scale: number; triggerWord: string | null }>

    const out: Array<{ id: string; scale: number; triggerWord: string | null }> = []
    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      if (nodeEntry.type !== 'LoraModel') continue
      const sourceData = nodeEntry.data as Record<string, unknown>
      const sourceState = sourceData?.state as Record<string, unknown> | undefined
      if (!sourceState) continue

      const loraId = sourceState.loraId as string | undefined
      if (!loraId) continue
      const scale = typeof sourceState.scale === 'number' ? (sourceState.scale as number) : 1
      const triggerWord = (sourceState.triggerWord as string | null | undefined) ?? null
      out.push({ id: loraId, scale, triggerWord })
    }
    return out
  }, [sourceNodesData])

  // Auto-clear validation errors when inputs change (image connected, prompt typed)
  useEffect(() => {
    if (orchestratorState.status === 'error') {
      setOrchestratorState({ status: 'idle' })
    }
  // Only trigger on input changes, not on orchestratorState itself
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedImage, connectedPrompt])

  const handleChange = useCallback((dataOrUpdater: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => {
    if (typeof dataOrUpdater === 'function') {
      onStateChange((prev) => {
        const result = dataOrUpdater(prev)
        if (result.prompt !== undefined) currentPromptRef.current = result.prompt as string
        if (result.orchestrator !== undefined) {
          setOrchestratorState(result.orchestrator as NodeOrchestratorState)
          const { orchestrator, ...rest } = result
          return rest
        }
        return result
      })
      return
    }

    const d = dataOrUpdater
    if (d.prompt !== undefined) {
      currentPromptRef.current = d.prompt as string
      onStateChange(prev => ({ ...prev, prompt: d.prompt }))
    }
    if (d.title !== undefined) {
      onStateChange(prev => ({ ...prev, title: d.title, titleSource: 'custom', data: { ...(prev.data as Record<string, unknown> || {}), title: d.title } }))
    }
    if (d.images !== undefined) onStateChange(prev => ({ ...prev, images: d.images }))
    if (d.imageRoles !== undefined) onStateChange(prev => ({ ...prev, imageRoles: d.imageRoles }))
    if (d.imageAssets !== undefined) onStateChange(prev => ({ ...prev, imageAssets: d.imageAssets }))
    if (d.createdItems !== undefined) onStateChange(prev => ({ ...prev, createdItems: d.createdItems }))
    if (d.collapsed !== undefined) onStateChange(prev => ({ ...prev, collapsed: d.collapsed }))
    if (d.orchestrator !== undefined) setOrchestratorState(d.orchestrator as NodeOrchestratorState)
  }, [onStateChange])

  const handleSelect = useCallback(() => {}, [])

  // Resolve active model for cost estimation (must be declared before handleGenerate which references costEstimate)
  const defaultModel = useMemo(() => getDefaultModel(imageModels), [imageModels])
  const activeModel = useMemo(() => {
    const savedModelId = typeof genieState.modelId === 'string' ? genieState.modelId : undefined
    const savedModelValid = savedModelId && imageModels.some(m => m.id === savedModelId)
    const resolvedId = (savedModelValid ? savedModelId : undefined) || defaultModel?.id
    if (!resolvedId) return defaultModel
    return findModelById(imageModels, resolvedId) || defaultModel
  }, [imageModels, genieState.modelId, defaultModel])

  // Cost estimate for insufficient balance dialog
  const { estimate: costEstimate } = useJobEstimate(activeModel?.provider ?? null, activeModel?.id ?? null, genieState.settings as Record<string, unknown> | undefined)
  // Ref for error-path-only value — avoids handleGenerate recreation on cost recalc
  const costEstimateRef = useRef(costEstimate)
  costEstimateRef.current = costEstimate
  const styleTransferRef = useRef<StyleTransferPresentation | null>(null)
  const inpaintRef = useRef<InpaintPresentation | null>(null)

  // Pre-flight balance check helper
  const showInsufficientBalance = useInsufficientBalanceStore((s) => s.showInsufficientBalance)

  const handleGenerate = useCallback(async () => {
    // Prevent concurrent double-click generation
    if (isGeneratingRef.current) return
    isGeneratingRef.current = true

    const bridge = useCanvasBridgeStore.getState()
    const inputData = getInputNode(id, reactFlowInstance.getNodes(), reactFlowInstance.getEdges())

    const inputPrompt = Array.isArray(inputData.prompt) ? inputData.prompt[0] : inputData.prompt
    const finalPrompt = inputPrompt || connectedPrompt || currentPromptRef.current

    // Resolve image(s) from inputData — check both canonical keys and model parameter names
    // getInputNode maps handles to model param names (e.g., 'image_url', 'image_urls') not just 'image'
    const resolveImageData = (): { single?: string; array?: string[] } => {
      for (const key of Object.keys(inputData)) {
        if (key === 'prompt' || key === 'systemPrompt' || key === 'mask') continue
        const val = inputData[key]
        if (Array.isArray(val)) {
          const urls = val.filter((v): v is string => typeof v === 'string' && v.startsWith('http'))
          if (urls.length > 0) return { single: urls[0], array: urls }
        }
        if (typeof val === 'string' && val.startsWith('http')) return { single: val, array: [val] }
      }
      return {}
    }
    const imageData = resolveImageData()
    const finalImage = (Array.isArray(inputData.image) ? inputData.image[0] : inputData.image) || imageData.single
    const finalImages = inputData.images || imageData.array
    const finalMask = Array.isArray(inputData.mask) ? inputData.mask[0] : inputData.mask

    const promptString = typeof finalPrompt === 'string' ? finalPrompt : ''
    const styleTransfer = styleTransferRef.current
    const inpaint = inpaintRef.current
    const hasImage = styleTransfer
      ? Boolean(styleTransfer.sourceUrl)
      : inpaint
        ? Boolean(inpaint.sourceUrl)
        : !!finalImage || !!connectedImage || (finalImages && finalImages.length > 0) || (genieState.images && (genieState.images as string[]).length > 0)

    // Purpose-specific validation
    const needsPrompt = purpose === 'create' || purpose === 'edit' || !purpose
    const needsImage = purpose === 'edit' || purpose === 'enhance' || purpose === 'tools'

    if (needsPrompt && !promptString?.trim()) {
      setOrchestratorState({ status: 'error', message: 'Please enter a prompt or connect a text node' })
      isGeneratingRef.current = false
      return
    }

    if (needsImage && !hasImage) {
      setOrchestratorState({ status: 'error', message: styleTransfer?.blockReason || inpaint?.blockReason || 'Please upload or connect an input image' })
      isGeneratingRef.current = false
      return
    }
    if (styleTransfer?.blockReason && styleTransfer.sourceUrl) {
      setOrchestratorState({ status: 'error', message: styleTransfer.blockReason })
      isGeneratingRef.current = false
      return
    }
    if (inpaint?.blockReason) {
      setOrchestratorState({ status: 'error', message: inpaint.blockReason })
      isGeneratingRef.current = false
      return
    }

    const panelSettings = bridge.settingsApi?.getSettings()
    const defaultModel = getDefaultModel(imageModels)
    // Fix 6: Read from ref instead of closure over genieState
    const currentGenieState = genieStateRef.current
    const savedModelValid = currentGenieState.modelId && imageModels.some(m => m.id === currentGenieState.modelId)
    const resolvedModelId = (panelSettings?.modelId as string) || (savedModelValid ? currentGenieState.modelId : undefined) || defaultModel?.id
    if (!resolvedModelId) {
      toast.error('Please select a model before generating')
      setOrchestratorState({ status: 'error', message: 'No model selected' })
      isGeneratingRef.current = false
      return
    }

    const currentSettings = {
      modelId: resolvedModelId as string,
      modelFamilySlug: (panelSettings?.modelFamilySlug as string) || (currentGenieState.modelFamilySlug as string) || (currentGenieState.brandFamily as string) || defaultModel?.modelFamilySlug || '',
      settings: (panelSettings?.settings as Record<string, unknown>) || (currentGenieState.settings as Record<string, unknown>) || {},
    }

    const outputCount = (currentSettings.settings?.iterations as number) || 1

    // When generating multiple images, omit the seed so each gets a unique result.
    const genParameters = { ...currentSettings.settings }
    if (outputCount > 1 && genParameters.seed !== undefined) {
      delete genParameters.seed
    }

    // Merge connected LoRA nodes into settings.loras as {id, scale}[].
    // Falls back to any existing settings.loras from the panel (backward compat for
    // legacy canvases that still carry a string[] there).
    let mergedLoras: Array<{ id: string; scale: number }> | undefined
    if (connectedLoras.length > 0) {
      mergedLoras = connectedLoras.map(({ id: lid, scale }) => ({ id: lid, scale }))
    } else if (Array.isArray(genParameters.loras) && genParameters.loras.length > 0) {
      mergedLoras = (genParameters.loras as Array<string | { id: string; scale?: number }>).map((entry) =>
        typeof entry === 'string' ? { id: entry, scale: 1 } : { id: entry.id, scale: entry.scale ?? 1 },
      )
    }
    if (mergedLoras) {
      genParameters.loras = mergedLoras
    } else {
      delete genParameters.loras
    }

    // Prepend any LoRA trigger words the user hasn't already typed.
    let effectivePrompt = promptString
    if (connectedLoras.length > 0) {
      const missingTriggers = connectedLoras
        .map((l) => l.triggerWord?.trim())
        .filter((t): t is string => !!t && !effectivePrompt.toLowerCase().includes(t.toLowerCase()))
      if (missingTriggers.length > 0) {
        effectivePrompt = `${missingTriggers.join(' ')} ${effectivePrompt}`.trim()
      }
    }

    const selectedModel = imageModels.find(model => model.id === currentSettings.modelId)
    const capability = resolveImageInputCapability(selectedModel)
    const execution = executionForProvider(selectedModel?.provider)
    const roles = (currentGenieState.imageRoles as Record<string, string> | undefined) ?? {}
    const assets = (currentGenieState.imageAssets as Record<string, { name?: string; contentType?: string; bytes?: number; cloudUrl?: string }> | undefined) ?? {}
    const selectedUrls = Array.isArray(currentGenieState.images)
      ? currentGenieState.images.filter((url): url is string => typeof url === 'string')
      : []
    const handleUrls = [
      ...(typeof finalImage === 'string' ? [finalImage] : []),
      ...(Array.isArray(finalImages) ? finalImages.filter((url): url is string => typeof url === 'string') : []),
    ]
    const attachments = hydrateAttachments(selectedUrls, assets, roles, [...new Set([...connectedImageUrls, ...handleUrls])])
    const imagePlan = planImageInputs(capability, attachments, execution)
    if (imagePlan.blocked) {
      setOrchestratorState({ status: 'error', message: imagePlan.explanation ?? 'Resolve the image inputs before running.' })
      isGeneratingRef.current = false
      return
    }
    const settings: Record<string, unknown> = { ...genParameters }
    delete settings.image
    delete settings.images
    delete settings.mask
    if (imagePlan.fields) Object.assign(settings, imagePlan.fields)
    if (finalMask && settings.mask === undefined) settings.mask = finalMask

    if (styleTransfer) {
      settings.image = styleTransfer.sourceUrl
      settings.image_url = styleTransfer.sourceUrl
      delete settings.images
      if (styleTransfer.supportsReference && styleTransfer.referenceUrl) {
        settings.style_reference_image_url = styleTransfer.referenceUrl
      } else {
        delete settings.style_reference_image_url
      }
    }
    if (inpaint) {
      delete settings.image
      delete settings.images
      delete settings.mask
      for (const key of Object.keys(settings)) {
        if (isInpaintMaskParam(key) || key === 'image_url' || key === 'mask_image_url') delete settings[key]
      }
      if (inpaint.sourceUrl && inpaint.sourceParam) {
        settings.image = inpaint.sourceUrl
        settings[inpaint.sourceParam] = inpaint.sourceUrl
      }
      if (inpaint.supportsMask && inpaint.maskUrl && inpaint.maskParam) {
        settings[inpaint.maskParam] = inpaint.maskUrl.startsWith('data:image/')
          ? await flattenPaintedMask(inpaint.maskUrl)
          : inpaint.maskUrl
      }
      if (inpaint.sourceUrl && inpaint.maskUrl && typeof Image !== 'undefined') {
        const [sourceSize, maskSize] = await Promise.all([
          loadImageSize(inpaint.sourceUrl),
          loadImageSize(inpaint.maskUrl),
        ])
        const alignment = inpaintAlignmentBlock(sourceSize, maskSize)
        if (alignment) {
          setOrchestratorState({ status: 'error', message: alignment })
          isGeneratingRef.current = false
          return
        }
      }
    }
    try {
      const jobId = await submitJob({
        nodeId: id,
        nodeType: 'image',
        purpose,
        subPurpose,
        prompt: effectivePrompt,
        modelId: currentSettings.modelId,
        settings,
        variantCount: outputCount,
      })

      if (!jobId) {
        setOrchestratorState({ status: 'error', message: 'Failed to submit generation job' })
        isGeneratingRef.current = false
        return
      }

      // Create placeholder child nodes for optimistic UI
      const placeholderIds: string[] = bridge.createPlaceholderCallback?.({
        parentNodeId: id,
        type: 'image',
        count: outputCount,
      }) ?? []

      if (placeholderIds.length === 0) {
        console.warn('[ImageNode] Placeholder creation returned no nodes — results may not display on canvas')
      }

      // Store placeholder mapping so WebSocket handler can update them
      const jobStore = useJobStore.getState()
      jobStore.setPlaceholders(jobId, id, placeholderIds)

      // Lock parent node and set queued state
      onStateChange(prev => ({
        ...prev,
        locked: true,
        prompt: promptString,
        modelId: currentSettings.modelId,
        settings: currentSettings.settings,
      }))
      setOrchestratorState({ status: 'working', message: 'Queued for generation...', progress: 0, startedAt: new Date() })
    } catch (err) {
      // Always unlock the node and reset guard on any error
      onStateChange(prev => ({ ...prev, locked: false }))
      isGeneratingRef.current = false

      if (isApiError(err, 'INSUFFICIENT_BALANCE')) {
        setOrchestratorState({ status: 'idle' })
        showInsufficientBalance(costEstimateRef.current?.priceUsd || '—', walletBalanceRef.current || '0')
      } else {
        console.error('Job submission failed:', err)
        setOrchestratorState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to start generation' })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitJob, id, onStateChange, connectedPrompt, connectedImage, connectedImageUrls, connectedLoras, imageModels, purpose, subPurpose, showInsufficientBalance])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts
  const handleGenerateRef = useRef(handleGenerate)
  handleGenerateRef.current = handleGenerate

  useEffect(() => {
    if (!action) return
    switch (action.type) {
      case 'generate': handleGenerateRef.current(); break
      case 'cancel': setOrchestratorState({ status: 'idle' }); break
      case 'clear': onStateChange(prev => ({ ...prev, createdItems: [] })); break
      case 'update':
        if (action.params?.prompt !== undefined) {
          currentPromptRef.current = action.params.prompt
          onStateChange(prev => ({ ...prev, prompt: action.params.prompt }))
        }
        break
    }
    onStateChange(prev => ({ ...prev, _action: null }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTimestamp, action, onStateChange])

  // Pinned parameters support
  const pinnedNames = useMemo(() => {
    const raw = genieState.pinnedParameters
    return Array.isArray(raw) ? (raw as string[]) : []
  }, [genieState.pinnedParameters])

  const { parameters: pinnedParams, values: pinnedValues } = usePinnedParameters({
    pinnedNames,
    nodeType: 'image',
    modelFamilySlug: (genieState.modelFamilySlug as string | undefined) || (genieState.brandFamily as string | undefined) || activeModel?.modelFamilySlug,
    modelId: (genieState.modelId as string | undefined) || activeModel?.id,
    purpose: (purpose as AiModelPurpose) || 'create',
    settings: genieState.settings as Record<string, unknown> | undefined,
  })

  const handlePinnedParameterChange = useCallback((paramName: string, value: unknown) => {
    onStateChange(prev => ({
      ...prev,
      settings: {
        ...((prev.settings as Record<string, unknown>) || {}),
        [paramName]: value,
      },
    }))
  }, [onStateChange])

  // Model dropdown support for pinned model
  const pinnedModelsList = useMemo(() => {
    if (!pinnedNames.includes('__model__')) return undefined
    return imageModels.map(m => ({ id: m.id, name: m.name }))
  }, [imageModels, pinnedNames])

  const handlePinnedModelChange = useCallback((modelId: string) => {
    const model = findModelById(imageModels, modelId)
    if (!model) return
    onStateChange(prev => ({
      ...prev,
      ...modelTitleState(prev, model.name),
      modelId: model.id,
      modelFamilySlug: model.modelFamilySlug,
    }))
  }, [imageModels, onStateChange])

  const nodeProps = {
    id,
    selected: selected || genieState.selected,
    initialData: {
      ...genieState,
      ...(activeModel?.name ? modelTitleState(genieState, activeModel.name) : {}),
    },
    connectedPrompt,
    connectedImage,
    orchestratorState,
    onChange: handleChange,
    onStateChange,
    onSelect: handleSelect,
    onGenerate: handleGenerate,
    onHoverChange,
    loading,
    error,
    canvasId: (genieState.canvasId as string) || canvasId,
    costProvider: activeModel?.provider ?? null,
    costModel: activeModel?.id ?? null,
    walletBalance: walletBalance ?? undefined,
    pinnedParameters: pinnedParams.length > 0 ? pinnedParams : undefined,
    pinnedParameterValues: pinnedParams.length > 0 ? pinnedValues : undefined,
    onPinnedParameterChange: (pinnedParams.length > 0 || pinnedNames.includes('__model__')) ? handlePinnedParameterChange : undefined,
    pinnedModelName: pinnedNames.includes('__model__') ? (activeModel?.name || activeModel?.id || 'No model') : undefined,
    pinnedModels: pinnedModelsList,
    onPinnedModelChange: pinnedNames.includes('__model__') ? handlePinnedModelChange : undefined,
    imageCapability: resolveImageInputCapability(activeModel),
    imageExecution: executionForProvider(activeModel?.provider),
    connectedImageUrls,
  }

  const purposeHandles = (purpose && HANDLES_BY_PURPOSE[purpose]) || DEFAULT_IMAGE_HANDLES
  // Merge persisted handles with the current purpose handles so schema additions
  // (e.g. 'loras-input') appear on canvases saved before the handle was introduced.
  const handles: HandleMeta[] = useMemo(() => {
    const persisted = nodeData.handles
    const base = !persisted || persisted.length === 0
      ? purposeHandles
      : (() => {
          const existingIds = new Set(persisted.map((h) => h.id))
          const missing = purposeHandles.filter((h) => !existingIds.has(h.id))
          return missing.length === 0 ? persisted : [...persisted, ...missing]
        })()
    return ensureInpaintHandles(base, subPurpose)
  }, [nodeData.handles, purposeHandles, subPurpose])

  const styleTransfer = useMemo(() => {
    if (subPurpose !== 'style-transfer') return null
    const connections = incomingConnections.flatMap((connection) => {
      if (!connection.targetHandle) return []
      const source = sourceNodesData.find((node) => node?.id === connection.source)
      const url = imageUrlFromFlowNode(source ? { type: source.type, data: source.data } : undefined)
      return [{ handleId: connection.targetHandle, url, pending: !url }]
    })
    return presentStyleTransfer({
      subPurpose,
      handles: handles.map((handle) => ({
        id: handle.id,
        type: handle.type,
        dataType: handle.dataType,
        parameterName: handle.metadata?.parameterName,
        ordered: typeof handle.position === 'object' && handle.position && 'ordered' in handle.position ? handle.position.ordered : undefined,
      })),
      connections,
    })
  }, [subPurpose, handles, incomingConnections, sourceNodesData])
  styleTransferRef.current = styleTransfer

  const inpaint = useMemo(() => {
    if (subPurpose !== 'inpaint') return null
    const connections = incomingConnections.flatMap((connection) => {
      if (!connection.targetHandle) return []
      const source = sourceNodesData.find((node) => node?.id === connection.source)
      const asset = inpaintAssetFromNode(source ? { type: source.type, data: source.data } : undefined)
      return [{ handleId: connection.targetHandle, ...asset }]
    })
    const schema = activeModel?.parameterSchema as { inputs?: Array<{ name?: string; required?: boolean }> } | undefined
    const inputs = Array.isArray(schema?.inputs) ? schema.inputs : []
    const selected = Array.isArray(genieState.images) ? genieState.images.find((url): url is string => typeof url === 'string' && /^https?:\/\//.test(url)) : undefined
    return presentInpaint({
      subPurpose,
      paramNames: inputs.map((item) => item.name).filter((name): name is string => Boolean(name)),
      required: inputs.filter((item) => item.required && item.name).map((item) => item.name as string),
      selectedSourceUrl: selected,
      savedFields: genieState.settings as Record<string, unknown> | undefined,
      handles: handles.map((handle) => ({
        id: handle.id,
        type: handle.type,
        dataType: handle.dataType,
        parameterName: handle.metadata?.parameterName,
        ordered: typeof handle.position === 'object' && handle.position && 'ordered' in handle.position ? handle.position.ordered : undefined,
      })),
      connections,
    })
  }, [subPurpose, handles, incomingConnections, sourceNodesData, activeModel, genieState.images, genieState.settings])
  inpaintRef.current = inpaint

  const displayHandles = useMemo(() => {
    const labeled = styleTransfer?.supportsReference ? styleTransfer : inpaint
    if (!labeled) return handles
    return handles.map((handle) => {
      const role = labeled.roles.find((item) => item.handleId === handle.id && item.role !== 'unused')
      if (!role) return handle
      return {
        ...handle,
        label: labeled === styleTransfer ? role.tooltip : role.label,
        description: role.tooltip,
        metadata: {
          ...handle.metadata,
          parameterName: role.param ?? handle.metadata?.parameterName,
          parameterLabel: role.label,
          icon: role.role === 'mask' ? 'circle-dashed' : role.role === 'reference' ? 'image-plus' : handle.metadata?.icon,
        },
      }
    })
  }, [handles, styleTransfer, inpaint])

  const rolePreviews = (styleTransfer ?? inpaint)?.roles.map((role) => ({
    label: role.label,
    url: role.url,
    status: role.status,
    message: role.message,
  }))
  const roleNote = styleTransfer?.precedence || styleTransfer?.limitation || inpaint?.limitation

  return (
    <>
      <NodeHandles nodeId={id} handles={displayHandles} />
      <ImageNode {...nodeProps} purpose={purpose} rolePreviews={rolePreviews} roleNote={roleNote} />
    </>
  )
}

export const ImageNodeNode = React.memo(
  ImageNodeNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as ImageNodeNodeData
    const nextData = nextProps.data as unknown as ImageNodeNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.titleSource === nextData.state.titleSource &&
      prevData.state.modelName === nextData.state.modelName &&
      prevData.state.prompt === nextData.state.prompt &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.locked === nextData.state.locked &&
      prevData.state.images === nextData.state.images &&
      prevData.state.createdItems === nextData.state.createdItems &&
      prevData.state._action === nextData.state._action &&
      prevData.state.pinnedParameters === nextData.state.pinnedParameters &&
      prevData.state.settings === nextData.state.settings &&
      prevData.state.purpose === nextData.state.purpose &&
      prevData.state.modelId === nextData.state.modelId &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)
ImageNodeNode.displayName = 'ImageNodeNode'
